import { useState } from "react";
import { api } from "../api.js";
import { fmt, t } from "../i18n.js";
import { Confirm, ErrorBox, Field, Loader, Tabs, useAction, useLoad } from "../ui.jsx";

const COMP = [["punctuality", "الالتزام بالمواعيد"], ["quality", "جودة التنفيذ"], ["documentation", "اكتمال التوثيق"], ["workload", "إنجاز حجم العمل"]];
const PARAMS = [["min_approved_tasks", "أقل عدد مهام معتمدة لإظهار نتيجة"], ["late_grace_minutes", "سماحية التأخير (دقائق)"],
  ["late_full_penalty_hours", "التأخير الذي يجعل الالتزام صفرًا (ساعات)"], ["execution_error_penalty", "خصم الجودة لكل إعادة بخطأ تنفيذي"],
  ["documentation_return_penalty", "خصم التوثيق لكل إعادة بنقص توثيق"]];

export function Settings() {
  const [tab, setTab] = useState("kpi");
  return (
    <div className="page">
      <header className="page-head"><h1>{t("settings")}</h1></header>
      <Tabs value={tab} onChange={setTab} tabs={[["kpi", "قواعد التقييم"], ["periods", "الفترات الشهرية"], ["system", "حالة النظام"]]} />
      {tab === "kpi" && <Rules />}
      {tab === "periods" && <Periods />}
      {tab === "system" && <SystemStatus />}
    </div>
  );
}

function Rules() {
  const state = useLoad(() => api.get("/kpi/rules"), []);
  const [role, setRole] = useState("");
  const [form, setForm] = useState(null);
  const { busy, error, run } = useAction();
  const startEdit = (rows) => {
    const cur = rows.find((r) => r.active && (r.applies_to_role || "") === role) || rows.find((r) => r.active && !r.applies_to_role);
    setForm({ name: role ? `قواعد ${t("role." + role)}` : "القواعد الافتراضية", weights: { ...cur.weights }, params: { ...cur.params } });
  };
  const total = form ? COMP.reduce((s, [k]) => s + Number(form.weights[k] || 0), 0) : 0;
  return (
    <Loader state={state}>{(d) => (
      <>
        <section className="panel">
          <h2>القواعد الحالية</h2>
          <table className="table"><thead><tr><th>القواعد</th><th>الدور</th>{COMP.map(([k, l]) => <th key={k}>{l}</th>)}<th>منذ</th><th>الحالة</th></tr></thead>
            <tbody>{d.items.map((r) => (
              <tr key={r.id} className={r.active ? "" : "muted"}><td>{r.name}</td><td>{r.applies_to_role ? t("role." + r.applies_to_role) : "الجميع"}</td>
                {COMP.map(([k]) => <td key={k}>{r.weights[k]}%</td>)}<td>{fmt.date(r.created_at)}</td><td>{r.active ? "مفعّلة" : "نسخة سابقة"}</td></tr>))}</tbody></table>
          <p className="muted small">كل تعديل يُنشئ نسخة جديدة. الأشهر المغلقة تحتفظ بالنسخة التي حُسبت بها ولا تتغير نتائجها.</p>
        </section>
        <section className="panel">
          <h2>إنشاء نسخة جديدة</h2>
          <div className="inline-form">
            <select value={role} onChange={(e) => { setRole(e.target.value); setForm(null); }} aria-label="الدور">
              <option value="">لجميع الأدوار (افتراضي)</option>
              {["employee", "team_lead", "project_manager"].map((r) => <option key={r} value={r}>{t("role." + r)}</option>)}
            </select>
            <button className="btn" onClick={() => startEdit(d.items)}>ابدأ من القواعد الحالية</button>
          </div>
          {form && (
            <div className="stack">
              <Field label="اسم النسخة"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <div className="form-grid">{COMP.map(([k, l]) => (
                <Field key={k} label={`${l} (%)`}><input type="number" min={0} max={100} value={form.weights[k]}
                  onChange={(e) => setForm({ ...form, weights: { ...form.weights, [k]: e.target.value } })} /></Field>))}</div>
              <p className={total === 100 ? "muted" : "late"}>مجموع الأوزان: {total}%</p>
              <div className="form-grid">{PARAMS.map(([k, l]) => (
                <Field key={k} label={l}><input type="number" min={0} value={form.params[k]}
                  onChange={(e) => setForm({ ...form, params: { ...form.params, [k]: e.target.value } })} /></Field>))}</div>
              <ErrorBox error={error} />
              <button className="btn btn-primary" disabled={busy || total !== 100} onClick={() => run(() => api.put("/kpi/rules", {
                name: form.name, applies_to_role: role || null,
                weights: Object.fromEntries(COMP.map(([k]) => [k, Number(form.weights[k])])),
                params: Object.fromEntries(PARAMS.map(([k]) => [k, Number(form.params[k])])),
              }), "حُفظت نسخة القواعد الجديدة").then(() => { setForm(null); state.reload(); }, () => {})}>حفظ نسخة جديدة</button>
            </div>
          )}
        </section>
      </>
    )}</Loader>
  );
}

function Periods() {
  const state = useLoad(() => api.get("/kpi/periods"), []);
  const [confirm, setConfirm] = useState(null);
  const now = new Date(Date.now() + 2 * 36e5);
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
  return (
    <section className="panel">
      <p>إغلاق الشهر يحفظ نتائج جميع الموظفين ونسخة القواعد المستخدمة، ويرسل لكل موظف إشعارًا بأن تقييمه متاح (دون عرض النتيجة على شاشة القفل).</p>
      <button className="btn btn-primary" onClick={() => setConfirm({ month: last, action: "close" })}>إغلاق شهر {last}</button>
      <Loader state={state}>{(d) => (
        <table className="table"><thead><tr><th>الشهر</th><th>الحالة</th><th>أُغلق في</th><th /></tr></thead>
          <tbody>{d.items.map((p) => (
            <tr key={p.id}><td>{p.month.slice(0, 7)}</td><td>{p.status === "closed" ? "مغلق" : "مفتوح"}</td><td>{fmt.dt(p.closed_at)}</td>
              <td>{p.status === "closed"
                ? <button className="btn btn-sm" onClick={() => setConfirm({ month: p.month.slice(0, 7), action: "reopen" })}>إعادة فتح</button>
                : <button className="btn btn-sm" onClick={() => setConfirm({ month: p.month.slice(0, 7), action: "close" })}>إغلاق</button>}</td></tr>))}</tbody></table>
      )}</Loader>
      {confirm && <Confirm title={confirm.action === "close" ? `إغلاق شهر ${confirm.month}` : `إعادة فتح شهر ${confirm.month}`}
        needReason={confirm.action === "reopen"} danger={confirm.action === "reopen"}
        text={confirm.action === "reopen" ? "ستُحذف النتائج المحفوظة لهذا الشهر وتعود الأرقام مبدئية. يُسجَّل الإجراء في سجل التدقيق." : "لا يمكن إغلاق شهر لم ينتهِ بعد."}
        actionLabel={confirm.action === "close" ? "إغلاق الشهر" : "إعادة الفتح"}
        onConfirm={(reason) => api.post(`/kpi/periods/${confirm.month}/${confirm.action}`, confirm.action === "reopen" ? { reason } : {}).then(state.reload)}
        onClose={() => setConfirm(null)} />}
    </section>
  );
}

function SystemStatus() {
  const state = useLoad(() => api.get("/system/status"), []);
  return (
    <Loader state={state}>{(s) => (
      <section className="panel">
        <dl className="infos">
          <div className="info"><dt>البيئة</dt><dd>{s.environment}</dd></div>
          <div className="info"><dt>إشعارات الهاتف (Push)</dt><dd>{s.push_notifications === "configured" ? "مفعّلة" : "غير مفعّلة — الإشعارات داخل التطبيق فقط حتى إضافة مفاتيح Firebase"}</dd></div>
          <div className="info"><dt>تخزين الملفات</dt><dd>{s.storage_backend === "s3" ? "S3 (خاص)" : "قرص الخادم (خاص)"}</dd></div>
          <div className="info"><dt>الحد الأقصى للملف</dt><dd>{fmt.size(s.max_upload_bytes)} ({fmt.num(s.max_upload_bytes)} بايت)</dd></div>
          <div className="info"><dt>المنطقة الزمنية للعرض</dt><dd>{s.display_timezone}</dd></div>
        </dl>
      </section>
    )}</Loader>
  );
}
